<template>
	<v-card class="round-card background-2 mb-4">
		<v-card-title class="pa-3">
			<p class="pl-2 pl-xl-3 mb-0">Scadenze</p>
			<v-tooltip v-if="modified && fromXml" bottom max-width="320">
				<template v-slot:activator="{ on }">
					<v-chip v-on="on" small color="info" class="ml-3" outlined>
						<v-icon left small>mdi-file-document-outline</v-icon>dalle scadenze dell'XML
					</v-chip>
				</template>
				<span>Rate prese dal documento ricevuto: la condizione di pagamento non le riproduce, quindi non vengono ricalcolate in automatico.</span>
			</v-tooltip>
			<v-chip v-else-if="modified" small color="warning" class="ml-3" outlined>
				<v-icon left small>mdi-pencil</v-icon>modificate manualmente
			</v-chip>
			<v-spacer></v-spacer>
			<v-tooltip v-if="canCreateDeadline" top>
				<template v-slot:activator="{ on }">
					<v-btn icon v-on="on" @click="openCreate()"><v-icon>mdi-plus</v-icon></v-btn>
				</template>
				<span>Aggiungi rata</span>
			</v-tooltip>
			<regenerate-deadlines-dialog
				v-if="canCreateDeadline"
				:document-type="singularDocumentType"
				:document-id="documentId"
				@regenerated="getData()"
			></regenerate-deadlines-dialog>
			<v-btn icon @click="getData()"><v-icon>mdi-refresh</v-icon></v-btn>
		</v-card-title>
		<v-divider></v-divider>
		<!-- Scostamento mostrato sempre, non solo con rate manuali: anche una rata
			eliminata o una condizione malformata lasciano parte del dovuto senza scadenza -->
		<v-alert v-if="items.length && totalDifference < -0.01" type="warning" text dense class="ma-3">
			Importo scoperto: {{ -totalDifference | currency(2) }}. La somma delle rate ({{ deadlinesTotal | currency(2) }}) è inferiore al totale dovuto ({{ totalDue | currency(2) }}).
		</v-alert>
		<v-alert v-else-if="items.length && totalDifference > 0.01" type="warning" text dense class="ma-3">
			Rate eccedenti: {{ totalDifference | currency(2) }}. La somma delle rate ({{ deadlinesTotal | currency(2) }}) supera il totale dovuto ({{ totalDue | currency(2) }}).
		</v-alert>
		<v-data-table
			:headers="headers"
			:items="items"
			:loading="loading"
			hide-default-footer
			disable-pagination
			dense
			class="background-2">
			<template v-slot:item.actions="{ item }">
				<v-tooltip v-if="canEditDeadline" bottom>
					<template v-slot:activator="{ on, attrs }">
						<v-btn v-bind="attrs" v-on="on"
							icon
							small
							:class="$vuetify.breakpoint.smAndDown ? 'mx-2' : 'mx-0'"
							@click.native.stop="openEdit(item)">
							<v-icon :size="$vuetify.breakpoint.smAndDown ? 28 : 24" color="teal">edit</v-icon>
						</v-btn>
					</template>
					<span>Modifica</span>
				</v-tooltip>
				<v-tooltip v-if="canDeleteDeadline" bottom>
					<template v-slot:activator="{ on, attrs }">
						<v-btn v-bind="attrs" v-on="on"
							icon
							small
							:class="$vuetify.breakpoint.smAndDown ? 'mx-2' : 'mx-0'"
							@click.native.stop="askDelete(item)">
							<v-icon :size="$vuetify.breakpoint.smAndDown ? 28 : 24" color="pink">delete</v-icon>
						</v-btn>
					</template>
					<span>Elimina</span>
				</v-tooltip>
			</template>
			<template v-slot:item.expiration_date="{ item }">{{ item.expiration_date | date }}</template>
			<template v-slot:item.amount="{ item }">{{ item.amount | currency(2) }}</template>
			<template v-slot:item.paid="{ item }">{{ item.paid | currency(2) }}</template>
			<template v-slot:item.deadline_condition="{ item }">
				<v-chip v-if="conditionColor(item.deadline_condition)" small
					:color="conditionColor(item.deadline_condition)"
					:text-color="$eventBusUtility.lightOrDark(conditionColor(item.deadline_condition)) == 'dark' ? 'white' : 'black'">
					{{ item.deadline_condition_text || item.deadline_condition }}
				</v-chip>
				<v-chip v-else small text-color="white">{{ item.deadline_condition_text || item.deadline_condition }}</v-chip>
			</template>
			<template v-slot:item.manual="{ item }">
				<v-icon v-if="item.manual && fromXml" small color="info">mdi-file-document-outline</v-icon>
				<v-icon v-else-if="item.manual" small color="warning">mdi-pencil</v-icon>
			</template>
			<template v-slot:item.payment_method="{ item }">{{ item.payment_method_text || item.payment_method }}</template>
			<template v-slot:no-data>{{ noDataText }}</template>
		</v-data-table>
		<v-divider v-if="items.length"></v-divider>
		<!-- Stesso footer a riquadri della card Prodotti (FooterProductDocumentTable):
			totali di rate e pagamenti del documento, calcolati sulle righe in tabella -->
		<v-container v-if="items.length" class="py-3 px-3">
			<v-row class="pa-0 ma-0" justify="center">
				<v-col v-for="field in totalsFields" :key="field.key" class="py-0 totals-col">
					<v-card class="background-2" elevation="0">
						<v-card-text class="text-center pa-0">
							<v-icon :color="field.color" size="28" class="mb-1">{{ field.icon }}</v-icon>
							<div class="text-caption">{{ field.text }}</div>
							<div style="font-size: 16px !important" class="text-h6 font-weight-bold" :class="textColorClass(field.color)">
								{{ field.value | currency(2) }}
							</div>
						</v-card-text>
					</v-card>
				</v-col>
			</v-row>
		</v-container>

		<v-dialog v-model="createDialog" max-width="700" persistent>
			<v-card class="background-2 round-card" v-if="createDialog">
				<v-card-title class="pa-3">
					<p class="pl-2 pl-xl-3 mb-0">{{ editingId ? 'Modifica rata' : 'Nuova rata' }}</p>
					<v-spacer></v-spacer>
				</v-card-title>
				<v-divider></v-divider>
				<v-card-text class="pt-4">
					<fields-generator
						v-if="createFields"
						:fieldsset.sync="createFields"
						:model.sync="createModel"
						ref="createFieldsGenerator"
					></fields-generator>
					<!--
						Il campo "Importo Scadenza" e' generato genericamente da fields-generator:
						non espone uno slot per aggiungere un'azione dentro il campo stesso, quindi
						il tasto rapido resta qui sotto (allineato a destra, dove si trova l'importo)
						invece che dentro il fieldset condiviso.
					-->
					<div class="d-flex align-center justify-end mt-n2">
						<span class="caption grey--text mr-1">Importo residuo: {{ residualAmount | currency(2) }}</span>
						<v-tooltip bottom>
							<template v-slot:activator="{ on, attrs }">
								<v-btn v-bind="attrs" v-on="on"
									icon
									small
									color="primary"
									:disabled="residualAmount <= 0"
									@click="fillResidualAmount()">
									<v-icon small>mdi-cash-fast</v-icon>
								</v-btn>
							</template>
							<span>Importo residuo</span>
						</v-tooltip>
					</div>
				</v-card-text>
				<v-divider></v-divider>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn text color="info" @click.native.stop="createDialog = false; editingId = null">Annulla</v-btn>
					<v-btn text color="primary" @click.native.stop="saveDeadline()" :loading="creating">{{ editingId ? 'Salva' : 'Crea' }}</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<confirm-dialog
			:confirmModel="confirmDelete"
			mainTitle="Eliminare questa rata?"
			:secondTitle="deleteMessage"
			@update:confirmModel="confirmDelete = false"
			:atConfirm="deleteDeadline"
			:confirm="{ color: 'error', label: 'elimina' }"
		></confirm-dialog>
	</v-card>
</template>

<script>
	import RegenerateDeadlinesDialog from '@/components/modules/invoices/deadlines/RegenerateDeadlinesDialog'

	export default {
		name: 'document-deadlines-table',
		components: { RegenerateDeadlinesDialog },
		props: {
			documentType: { type: String, required: true },
			documentId: { type: Number, required: true },
			model: { type: Object, default: () => ({}) },
		},
		data() {
			return {
				items: [],
				// rate manuali ma identiche alle scadenze dell'XML ricevuto
				fromXml: false,
				loading: false,
				confirmDelete: false,
				toDelete: null,
				createDialog: false,
				createFields: null,
				createModel: {},
				creating: false,
				// id della rata in modifica nel dialog; null = nuova rata
				editingId: null,
				// general_types di deadline_condition dalla -info del crud scadenze:
				// stessi colori configurati che usa il crud
				conditionTypes: {},
				headers: [
					{ text: '', value: 'actions', sortable: false },
					{ text: 'Rata', value: 'installment' },
					{ text: 'Scadenza', value: 'expiration_date' },
					{ text: 'Importo', value: 'amount', align: 'end' },
					{ text: 'Pagato', value: 'paid', align: 'end' },
					{ text: 'Stato', value: 'deadline_condition' },
					{ text: 'Metodo', value: 'payment_method' },
					{ text: 'Manuale', value: 'manual', align: 'center' },
				],
			}
		},
		computed: {
			// route.params.doctype/modelClass arriva al plurale (es. 'saleinvoices'); gli
			// endpoint di rigenerazione scadenze e il controllo lato acquisto vogliono il singolare
			singularDocumentType() {
				return this.documentType.replace(/s$/, '')
			},
			// Documenti con rate del fornitore: crud Scadenze Fornitori. Le autofatture
			// non hanno scadenze e non mostrano questa card
			isSupplierSide() {
				return ['purchaseinvoice', 'suppliercreditnote'].includes(this.singularDocumentType)
			},
			// Lato acquisti le rate nascono al salvataggio; vendite e autofattura
			// solo all'emissione
			isPurchaseSide() {
				return ['purchaseinvoice', 'suppliercreditnote'].includes(this.singularDocumentType)
			},
			deadlinesModelDef() {
				return this.isSupplierSide ? 'supplierdeadlines' : 'companydeadlines'
			},
			// Senza questo messaggio un documento non emesso sembra aver perso le scadenze
			noDataText() {
				const notIssued = !this.isPurchaseSide && this.model.closed !== undefined && !this.model.closed
				if (!notIssued) {
					return 'Nessuna scadenza per questo documento'
				}
				return this.singularDocumentType === 'saleinvoice'
					? 'Le scadenze vengono generate quando la fattura viene emessa'
					: 'Le scadenze vengono generate quando il documento viene emesso'
			},
			// "Aggiungi rata"/"Elimina"/"Ricrea" chiamano endpoint protetti dal permesso
			// create/delete sul submodulo: senza questa guardia un ruolo senza il permesso
			// vede comunque i bottoni e riceve un 403 dall'interceptor.
			canCreateDeadline() {
				return this.$store.getters.checkRolePermissions(this.deadlinesModelDef, 'create')
			},
			canEditDeadline() {
				return this.$store.getters.checkRolePermissions(this.deadlinesModelDef, 'edit')
			},
			canDeleteDeadline() {
				return this.$store.getters.checkRolePermissions(this.deadlinesModelDef, 'delete')
			},
			modified() {
				return this.items.some(item => item.manual)
			},
			deadlinesTotal() {
				return this.items.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0)
			},
			totalDue() {
				// total_due puo' legittimamente valere 0 (documento saldato): con
				// || sarebbe scavalcato da model.total, che invece deve vincere
				// solo quando total_due non e' presente (null/undefined).
				const v = this.model.total_due != null ? this.model.total_due : this.model.total
				return parseFloat(v || 0)
			},
			totalDifference() {
				return this.deadlinesTotal - this.totalDue
			},
			paidTotal() {
				return this.items.reduce((sum, item) => sum + parseFloat(item.paid || 0), 0)
			},
			// quota delle rate scadute non ancora pagata
			expiredTotal() {
				return this.items
					.filter(item => item.deadline_condition === 'expired')
					.reduce((sum, item) => sum + parseFloat(item.amount || 0) - parseFloat(item.paid || 0), 0)
			},
			totalsFields() {
				const fields = [
					{ key: 'due', text: 'Totale dovuto', icon: 'mdi-receipt-text-outline', color: 'cyan', value: this.totalDue },
					{ key: 'deadlines', text: 'Totale rate', icon: 'mdi-calendar-multiple', color: 'blue', value: this.deadlinesTotal },
					{ key: 'paid', text: 'Pagato', icon: 'mdi-cash-check', color: 'green', value: this.paidTotal },
					{ key: 'residual', text: 'Da pagare', icon: 'mdi-cash-clock', color: 'orange darken-1', value: Math.max(0, this.deadlinesTotal - this.paidTotal) },
				]
				if (this.expiredTotal > 0.005) {
					fields.push({ key: 'expired', text: 'Scaduto', icon: 'mdi-alert-circle-outline', color: 'red', value: this.expiredTotal })
				}
				return fields
			},
			// deadlinesTotal esclude gia' le rate 'overpayment' (filtrate in getData): quello
			// che resta da coprire e' quindi il totale dovuto meno le rate normali esistenti,
			// senza scendere sotto zero (un documento gia' sovra-saldato non ha residuo).
			// Con pagamenti la rata si elimina scollegandoli: l'importo torna residuo
			// sui pagamenti, da riallocare, e va detto prima di confermare
			deleteMessage() {
				const paid = this.toDelete ? parseFloat(this.toDelete.paid || 0) : 0
				if (paid > 0) {
					return 'La rata ha pagamenti per ' + paid.toFixed(2).replace('.', ',') + ' €: verranno scollegati e l\'importo tornerà disponibile come residuo sui pagamenti. Le altre rate verranno rinumerate.'
				}
				return 'Le altre rate verranno rinumerate.'
			},
			residualAmount() {
				// in modifica l'importo attuale della rata torna disponibile
				const editing = this.editingId ? this.items.find(item => item.id === this.editingId) : null
				const others = this.deadlinesTotal - (editing ? parseFloat(editing.amount || 0) : 0)
				return Math.max(0, Math.round((this.totalDue - others) * 100) / 100)
			},
		},
		methods: {
			getData() {
				this.loading = true
				this.axios.post(this.deadlinesModelDef + '-filters', {
					paginate: false,
					sortBy: ['expiration_date'],
					sortDesc: [false],
					subFilters: { documents: this.documentId },
				}).then(response => {
					this.items = (response.data.data.data || []).filter(item => item.deadline_condition !== 'overpayment')
					this.loading = false
					this.checkXmlOrigin()
				}, () => {
					this.loading = false
				})
			},
			// Rate manuali uguali alle scadenze dell'XML ricevuto: non le ha toccate
			// nessuno, la condizione di pagamento semplicemente non le riproduce
			checkXmlOrigin() {
				if (!this.modified) {
					this.fromXml = false
					return
				}
				this.axios.post('regenerate-deadlines-data', { document_type: this.singularDocumentType, id: this.documentId, source: 'xml' }).then(response => {
					this.fromXml = !!(response.data.xml_available && response.data.matches_xml)
				}, () => {
					this.fromXml = false
				})
			},
			// come FooterProductDocumentTable: 'orange darken-1' -> 'orange--text text--darken-1'
			textColorClass(color) {
				const parts = color.split(' ')
				return parts.length > 1 ? parts[0] + '--text text--' + parts[1] : color + '--text'
			},
			conditionColor(condition) {
				const type = this.conditionTypes[condition]
				return type && type.extra_fields && type.extra_fields.color ? type.extra_fields.color : null
			},
			getConditionTypes() {
				this.axios.get(this.deadlinesModelDef + '-info').then(response => {
					const info = response.data.info || {}
					this.conditionTypes = (info.general_types && info.general_types.deadline_condition) || {}
				}, () => {})
			},
			// Riduce il fieldset completo di companydeadlines/supplierdeadlines ai soli campi
			// che il backend accetta per una rata manuale (vedi storeDocumentDeadline)
			buildCreateFields(fieldsset) {
				const allowedKeys = ['expiration_date', 'amount', 'payment_method', 'notes']
				const fields = allowedKeys
					.map(key => fieldsset.fields.find(field => field.key === key))
					.filter(field => !!field)
					.map(field => {
						const reducedField = { ...field }
						if (['expiration_date', 'amount'].includes(reducedField.key)) {
							reducedField.required = true
							reducedField.validations = 'required'
						} else if (reducedField.key === 'payment_method') {
							reducedField.required = false
							reducedField.validations = ''
							delete reducedField.always_required
						}
						return reducedField
					})
				return { ...fieldsset, fields }
			},
			resetCreateModel() {
				// moment e' in fuso locale: toISOString() darebbe il giorno prima a chi apre il dialog di notte
				this.createModel = { expiration_date: moment().format('YYYY-MM-DD') }
			},
			// $set: 'amount' non esiste ancora sul model appena resettato, e senza $set
			// l'assegnazione diretta non sarebbe reattiva (il campo non si aggiornerebbe a video)
			fillResidualAmount() {
				this.$set(this.createModel, 'amount', this.residualAmount)
			},
			// i campi del dialog sono gli stessi per nuova rata e modifica
			loadCreateFields() {
				if (this.createFields) {
					return Promise.resolve()
				}
				return this.axios.get(this.deadlinesModelDef + '/create').then(response => {
					this.createFields = this.buildCreateFields(response.data.fields)
				})
			},
			openCreate() {
				this.loadCreateFields().then(() => {
					this.editingId = null
					this.resetCreateModel()
					this.createDialog = true
				})
			},
			// Modifica nel dialog invece che nella pagina della scadenza: salvando
			// si resta sul documento
			openEdit(item) {
				Promise.all([
					this.loadCreateFields(),
					this.axios.get(this.deadlinesModelDef + '/' + item.id + '/edit'),
				]).then(([, response]) => {
					const model = response.data.model || {}
					this.editingId = item.id
					this.createModel = {
						expiration_date: model.expiration_date ? String(model.expiration_date).substring(0, 10) : null,
						amount: model.amount,
						payment_method: model.payment_method,
						notes: model.notes,
					}
					this.createDialog = true
				})
			},
			saveDeadline() {
				this.$refs.createFieldsGenerator.$validator.validateAll().then(result => {
					if (!result) {
						return
					}
					this.creating = true
					const request = this.editingId
						? this.axios.put(this.deadlinesModelDef + '/' + this.editingId, this.createModel)
						: this.axios.post(this.deadlinesModelDef, { document_id: this.documentId, ...this.createModel })
					request.then(response => {
						this.creating = false
						this.createDialog = false
						const detached = (response.data && response.data.detached_payments) || []
						if (detached.length) {
							this.$store.commit('setSnackbar', { timeout: 8000, text: 'Rata aggiornata: ' + detached.length + ' pagamenti sganciati, residuo da riallocare', value: true, color: 'warning' })
						} else {
							this.$store.commit('setSnackbar', { timeout: 4000, text: this.editingId ? 'Rata aggiornata' : 'Rata aggiunta', value: true, color: 'success' })
						}
						this.editingId = null
						this.getData()
					}, () => {
						// il messaggio di errore e' gia' mostrato dall'interceptor globale (main.js)
						this.creating = false
					})
				}).catch(() => {})
			},
			askDelete(item) {
				this.toDelete = item
				this.confirmDelete = true
			},
			deleteDeadline() {
				const detachPayments = parseFloat(this.toDelete.paid || 0) > 0
				this.axios.post(this.deadlinesModelDef + '/delete', { ids: [this.toDelete.id], detach_payments: detachPayments }).then(response => {
					this.confirmDelete = false
					const detached = (response.data && response.data.detached_payments) || []
					if (detached.length) {
						this.$store.commit('setSnackbar', { timeout: 8000, text: 'Rata eliminata: ' + detached.length + ' pagamenti scollegati, residuo da riallocare', value: true, color: 'warning' })
					} else {
						this.$store.commit('setSnackbar', { timeout: 4000, text: 'Rata eliminata', value: true, color: 'success' })
					}
					this.getData()
				}, error => {
					this.confirmDelete = false
					const message = (error.response && error.response.data && error.response.data.message) || 'Impossibile eliminare la rata'
					this.$store.commit('setSnackbar', { timeout: 8000, text: message, value: true, color: 'error' })
				})
			},
		},
		created() {
			this.getData()
			this.getConditionTypes()
		},
	}
</script>
<style scoped>
.totals-col {
	flex: 1 1 0;
	min-width: 110px;
}
</style>
